import { IconButton, Typography } from '@mui/material';
import { ChangeOptionsEnum, placementChangeOptions } from './Helper/Types';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { LayoutGrid, LayoutGridItem } from 'app/layout/LayoutGrid';
import React, { useEffect, useMemo, useState } from 'react';
import SearchIcon from '@mui/icons-material/Search';

import { FormInputDatePicker } from '@AMIEWEB/Candidate/CandidateProfile/CandidateTabPanel/PersonalInfoTab/Form/Controls/FormInputDatePicker';
import { FormInputDropdown } from '@AMIEWEB/Candidate/CandidateProfile/CandidateTabPanel/PersonalInfoTab/Form/Controls/FormInputDropdown';
import { missingField, PlacementChangeModalSourceIndentifier } from 'app/constants';
import { useStyles } from './Helper/Styles';
import { useTranslation } from 'react-i18next';
import { trackPromise, usePromiseTracker } from 'react-promise-tracker';
import { SessionKey, appendSessionValue, getSessionValue } from 'utils/customHooks/sessionStorage/sessionHelpers';
import { throttle } from 'lodash';
import { CustomTypeAhead, ICustomOption } from '../CreatePlacement/CustomComponents/CustomTypeAhead';
import { getOrderOptions } from 'app/services/OrderServices/OrderServices';
import { PlacementChangeSearchDialog } from './PlacementChangeSearchDialog';
import { getFacilitySearchLookups } from '@AMIEWEB/GlobalSearch/helper';
import { useSelector } from 'react-redux';
import { selectErrorMessage } from 'store/redux-store/new-placement/selectors';
import { selectPlacementDetails } from 'store/redux-store/placement-details/selectors';
import { ErrorMessage } from '@hookform/error-message';
import { addDays } from 'date-fns';
import {
  isAssignmentComplete,
  isGoodToGo,
  isPostponementWarning,
  isWaitingGoodToGo,
} from '../NextPlacementStatus/StatusDefaults';
import { selectPlacementStatus } from 'store/redux-store/placement-status/selectors';
import { selectUser } from 'oidc/user.selectors';

export const PlacementChangeControls = props => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  // const theme = useTheme();
  const [startDateDisabled, setStartDateDisabled] = useState<boolean>(true);
  const [nextControlDisabled, setNextControlDisabled] = useState<boolean>(true);
  const [options, setOptions] = useState<ICustomOption[]>([]);
  const { promiseInProgress: orderPromiseInProgress } = usePromiseTracker({ area: 'order-throttle-area', delay: 0 });
  const { promiseInProgress: facilityPromiseInProgress } = usePromiseTracker({
    area: 'facility-throttle-area',
    delay: 0,
  });
  const { promiseInProgress: submissionPromiseInProgress } = usePromiseTracker({
    area: 'placement-change-submit',
    delay: 0,
  });
  const [showHistory, setShowHistory] = useState<boolean>(true);
  const { searchDialogOpen, setSearchDialogOpen, setSaveDisabled, newPlacementId, searchResult, source } = props;
  const placementDetails = useSelector(selectPlacementDetails);
  const failureErrorMessage = useSelector(selectErrorMessage);
  const { currentPlacementStatus } = useSelector(selectPlacementStatus);
  const { userInfo } = useSelector(selectUser);
  const hasPlacementChangePermission = userInfo?.externalSystemDetails?.placementCopy;
  const changeOptionsPreStartCheck = useMemo(() => {
    return isWaitingGoodToGo(currentPlacementStatus) ||
      isPostponementWarning(currentPlacementStatus) ||
      isGoodToGo(currentPlacementStatus) ||
      isAssignmentComplete(currentPlacementStatus)
      ? true
      : false;
  }, [currentPlacementStatus]);
  const placementStartDate = useMemo(() => {
    return placementDetails?.header?.placementStartDate
      ? new Date(placementDetails?.header?.placementStartDate).getTime()
      : undefined;
  }, [placementDetails]);
  const placementEndDate = useMemo(() => {
    return placementDetails?.header?.placementEndDate
      ? new Date(placementDetails?.header?.placementEndDate).getTime()
      : undefined;
  }, [placementDetails]);

  const { setValue, getValues, clearErrors, setError, formState: { errors }, control } = useFormContext();
  const SetErrorMessage = (fieldName: string, errorMessage: string) => {
    setError(fieldName, {
      type: 'validate',
      message: errorMessage,
    });
    setNextControlDisabled(true);
    if (newPlacementId === null || newPlacementId <= 0) {
      setSaveDisabled(true);
    }
  };
  const clearAllErrors = () => {
    clearErrors('startDate');
    clearErrors('endDate');
    clearErrors('facilityID');
    clearErrors('orderID');
    clearErrors('errorMessage');
  };
  const startDateWatcher = useWatch({ name: 'startDate', control });
  const endDateWatcher = useWatch({ name: 'endDate', control });
  const { changeOption, startDate, endDate, facilityID, orderID } = getValues();
  const enableSaveOnChanges = () => {
    const startDateFrom = startDate && startDate !== '' ? new Date(startDate).getTime() : undefined;
    const endDateTo = endDate && endDate !== '' ? new Date(endDate).getTime() : undefined;
    const validateStartDate = () => {
      clearAllErrors();
      if (!startDateFrom && changeOption && startDate == null) {
        SetErrorMessage('startDate', t('placement.placementChange.startDateMandatory'));
        return false;
      } else if (startDateFrom && startDateFrom < placementStartDate && changeOption !== ChangeOptionsEnum.PreStart) {
        SetErrorMessage('startDate', t('placement.placementChange.startDateGreaterThanOrEqualToOldStartDate'));
        return false;
      } else if (startDateFrom && startDateFrom >= placementStartDate && changeOption === ChangeOptionsEnum.PreStart) {
        SetErrorMessage('startDate', t('placement.placementChange.startDateLessThanOldStartDate'));
        return false;
      } else if (startDateFrom && startDateFrom > placementEndDate && changeOption !== ChangeOptionsEnum.PreStart) {
        SetErrorMessage('startDate', t('placement.placementChange.startDateLessThanOrEqualToOldEndDate'));
        return false;
      } else if (startDateFrom) {
        return true;
      }
    };

    if (validateStartDate()) {
      if (changeOption === ChangeOptionsEnum.PayCycle) {
        setSaveDisabled(false);
      } else if (changeOption === ChangeOptionsEnum.FacilityID) {
        setNextControlDisabled(false);
        setSaveDisabled(facilityID ? false : true);
      } else if (changeOption === ChangeOptionsEnum.OrderID) {
        setNextControlDisabled(false);
        setSaveDisabled(orderID ? false : true);
      } else if (changeOption === ChangeOptionsEnum.PreStart) {
        if (!endDateTo && !nextControlDisabled) {
          SetErrorMessage('endDate', t('placement.placementChange.endDateMandatory'));
          setNextControlDisabled(false);
        } else if (startDateFrom && endDateTo && endDateTo > placementStartDate) {
          SetErrorMessage('endDate', t('placement.placementChange.endDateLessThanOldStartDate'));
          setNextControlDisabled(false);
        } else if (startDateFrom && endDateTo && startDateFrom > endDateTo) {
          SetErrorMessage('endDate', t('placement.placementChange.endDateGreaterThanOrEqualToStartDate'));
          setNextControlDisabled(false);
        } else if (startDate && endDate) {
          setNextControlDisabled(false);
          setSaveDisabled(false);
        } else if (startDate) {
          setNextControlDisabled(false);
          setSaveDisabled(true);
        }
      }
    }
  };

  useEffect(() => {
    enableSaveOnChanges();
  }, [startDateWatcher, endDateWatcher]);

  useEffect(() => {
    if (newPlacementId !== null) {
      setStartDateDisabled(true);
      setNextControlDisabled(true);
      setSaveDisabled(false);
    }
  }, [newPlacementId]);

  useEffect(() => {
    if (failureErrorMessage !== null) {
      clearAllErrors();
      setError('errorMessage', {
        type: 'validate',
        message: failureErrorMessage,
      });
      setStartDateDisabled(false);
      setNextControlDisabled(false);
      if(newPlacementId === null || newPlacementId <=0){
        setSaveDisabled(true);
      }
    }
  }, [failureErrorMessage]);

  useEffect(() => {
    if (searchDialogOpen && searchResult !== null) {
      setSaveDisabled(false);
    } else if (searchDialogOpen && searchResult == null) {
      setSaveDisabled(true);
    } else if (!searchDialogOpen && searchResult !== null) {
      autoPopulate();
      setSaveDisabled(false);
    } else if (!searchDialogOpen && searchResult == null) {
      setSaveDisabled(true);
    }
  }, [searchDialogOpen, searchResult]);

  const fetchOrderOptions = async request => {
    setShowHistory(false);
    await getOrderOptions(request).then(res =>
      setOptions(
        res.map(
          opt =>
            ({
              primaryLabel: opt.orderId.toString(),
              secondaryLabel: opt.facility,
            } as ICustomOption),
        ),
      ),
    );
  };

  const fetchFacilityOptions = async request => {
    setShowHistory(false);
    await getFacilitySearchLookups(request).then(res => {
      setOptions(
        res
          ?.filter(x => x.value?.startsWith(request))
          .map(
            opt =>
              ({
                primaryLabel: opt.value,
                secondaryLabel: opt.name?.slice(0, opt.name?.indexOf(',')).toString(),
              } as ICustomOption),
          ),
      );
    });
  };

  const throttleOrderService = useMemo(
    () => throttle(request => trackPromise(fetchOrderOptions(request), 'order-throttle-area'), 2000),
    [],
  );

  const throttleFacilityService = useMemo(
    () => throttle(request => trackPromise(fetchFacilityOptions(request), 'facility-throttle-area'), 2000),
    [],
  );

  const setSessionOptions = () => {
    clearErrors('errorMessage');
    setShowHistory(true);
    const recentSearch =
      changeOption === ChangeOptionsEnum.FacilityID
        ? getSessionValue(SessionKey.recentFacilities)
        : getSessionValue(SessionKey.recentOrders);
    const newOpts = recentSearch
      ?.filter(x => x.primaryLabel)
      ?.map(
        opt =>
          ({
            primaryLabel: opt?.primaryLabel,
            secondaryLabel: opt?.secondaryLabel,
          } as ICustomOption),
      );
    if (newOpts) {
      setOptions(newOpts);
    }
    return newOpts;
  };

  const autoPopulate = () => {
    const newOpts = setSessionOptions();
    if (newOpts?.length) {
      setValue(changeOption === ChangeOptionsEnum.FacilityID ? 'facilityID' : 'orderID', newOpts[0], {
        shouldDirty: true,
      });
      setSaveDisabled(false);
    }
  };

  const handlePlacementOptionsChange = event => {
    clearAllErrors();
    setValue('startDate', '');
    setValue('facilityID', null);
    setValue('orderID', null);
    setOptions([]);
    setStartDateDisabled(false);
    setNextControlDisabled(true);
    setSaveDisabled(true);
  };

  const handleStartDateChange = event => {
    setValue('startDate', event);
    if (changeOption === ChangeOptionsEnum.PreStart) {
      setValue('endDate', addDays(placementStartDate, -1));
    }
  };

  const handleSearchDialogOPen = () => {
    setSearchDialogOpen(true);
    setSaveDisabled(true);
  };

  const handleEndDateChange = event => {
    setValue('endDate', event);
  };

  useEffect(() => {
    if (source === PlacementChangeModalSourceIndentifier) {
      setValue('changeOption', 'Order ID');
      setValue('startDate', placementStartDate);
    }
  }, [source]);

  return (
    <>
      {searchDialogOpen && <PlacementChangeSearchDialog selectedChangeOption={changeOption} {...props} />}
      <LayoutGrid
        container
        direction="column"
        className={classes.root}
        spacing={4}
        style={{ marginTop: '10px', width: '390px', display: searchDialogOpen ? 'none' : 'inherit' }}
      >
        <LayoutGridItem item xs={5}>
          <Controller
            name="changeOption"
            control={control}
            render={({ field: { onChange, value, ...rest } }) => (
              <FormInputDropdown
                name="changeOption"
                label={t('placement.placementChange.changeTitle')}
                options={
                  hasPlacementChangePermission && changeOptionsPreStartCheck
                    ? placementChangeOptions.slice(0, 3)
                    : hasPlacementChangePermission
                    ? placementChangeOptions
                    : placementChangeOptions.slice(2, 3)
                }
                inputValue={changeOption || ''}
                handleChange={e => {
                  handlePlacementOptionsChange(e.target.value);
                  onChange(e.target.value);
                }}
                isError={errors?.changeOption?.message || ''}
                standard
                useMenuItem
                readOnly={newPlacementId !== null || submissionPromiseInProgress}
                disabled={source === PlacementChangeModalSourceIndentifier}
              />
            )}
          />
        </LayoutGridItem>

        <LayoutGridItem item xs={5}>
          <FormInputDatePicker
            name={'startDate'}
            label={t('placement.placementChange.startDateTitle')}
            handleChange={handleStartDateChange}
            isError={errors?.startDate?.message || ''}
            customClass={classes.datePickerInput}
            labelClass={classes.label}
            trailingIconVisible={true}
            inputValue={startDate}
            maxWidth={380}
            readOnly={
              source === PlacementChangeModalSourceIndentifier || startDateDisabled || submissionPromiseInProgress
            }
          />
        </LayoutGridItem>

        {changeOption === ChangeOptionsEnum.FacilityID ? (
          <LayoutGridItem item xs={5} style={{ maxHeight: '80px' }}>
            <LayoutGrid container direction="row" className={classes.root} spacing={4}>
              <LayoutGridItem item xs={7}>
                <Controller
                  name="facilityID"
                  defaultValue={options[0] || null}
                  control={control}
                  disabled={nextControlDisabled || submissionPromiseInProgress}
                  render={({ field: { onChange, ...rest } }) => (
                    <>
                      <CustomTypeAhead
                        style={{ backgroundColor: 'rgba(0,0,0, 0.04)' }}
                        onInputChange={throttleFacilityService}
                        options={options}
                        loading={facilityPromiseInProgress}
                        searchOnCharacterCount={3}
                        label={t('placement.placementChange.newFacilityTitle')}
                        disabled={nextControlDisabled}
                        onChange={(event, newValue) => {
                          onChange(newValue);
                          if (newValue) {
                            appendSessionValue(SessionKey.recentFacilities, newValue);
                            setSaveDisabled(false);
                          } else {
                            setSaveDisabled(true);
                          }
                        }}
                        {...rest}
                        showHistory={showHistory}
                        resetHistory={() => setSessionOptions()}
                        historyTitle={t('recentFacilities')}
                      />
                    </>
                  )}
                />
              </LayoutGridItem>
              <LayoutGridItem item xs={5}>
                <IconButton
                  color="primary"
                  disabled={nextControlDisabled || submissionPromiseInProgress}
                  disableTouchRipple
                  disableFocusRipple
                  size="medium"
                  className={classes.searchButton}
                  onClick={handleSearchDialogOPen}
                >
                  <SearchIcon />
                  <Typography>{t('placement.placementChange.facilitySearchIconTitle')}</Typography>
                </IconButton>
              </LayoutGridItem>
            </LayoutGrid>
          </LayoutGridItem>
        ) : (
          ''
        )}
        {changeOption === ChangeOptionsEnum.OrderID ? (
          <LayoutGridItem item xs={5} style={{ maxHeight: '80px' }}>
            <LayoutGrid container direction="row" className={classes.root} spacing={4}>
              <LayoutGridItem item xs={7}>
                <Controller
                  name="orderID"
                  defaultValue={options[0] || null}
                  control={control}
                  render={({ field: { onChange, ...rest } }) => (
                    <>
                      <CustomTypeAhead
                        style={{ backgroundColor: 'rgba(0,0,0, 0.04)' }}
                        onInputChange={throttleOrderService}
                        options={options}
                        loading={orderPromiseInProgress}
                        searchOnCharacterCount={3}
                        label={t('placement.placementChange.newOrderTitle')}
                        disabled={nextControlDisabled}
                        onChange={(event, newValue) => {
                          onChange(newValue);
                          if (newValue) {
                            appendSessionValue(SessionKey.recentOrders, newValue);
                            setSaveDisabled(false);
                          } else {
                            setSaveDisabled(true);
                          }
                        }}
                        {...rest}
                        showHistory={showHistory}
                        resetHistory={() => setSessionOptions()}
                        historyTitle={t('placement.create.dropdown.recentOrders')}
                      />
                    </>
                  )}
                />
              </LayoutGridItem>
              <LayoutGridItem item xs={5}>
                <IconButton
                  color="primary"
                  disableTouchRipple
                  disableFocusRipple
                  size="medium"
                  className={classes.searchButton}
                  onClick={handleSearchDialogOPen}
                  disabled={nextControlDisabled || submissionPromiseInProgress}
                >
                  <SearchIcon />
                  <Typography>{t('placement.placementChange.orderSearchIconTitle')}</Typography>
                </IconButton>
              </LayoutGridItem>
            </LayoutGrid>
          </LayoutGridItem>
        ) : (
          ''
        )}

        {changeOption === ChangeOptionsEnum.PreStart ? (
          <LayoutGridItem item xs={5}>
            <FormInputDatePicker
              name={'endDate'}
              label={t('placement.placementChange.endDateTitle')}
              handleChange={handleEndDateChange}
              isError={errors?.endDate?.message || ''}
              customClass={classes.datePickerInput}
              labelClass={classes.label}
              trailingIconVisible={true}
              inputValue={endDate}
              maxWidth={370}
              readOnly={nextControlDisabled || submissionPromiseInProgress}
            />
          </LayoutGridItem>
        ) : (
          ''
        )}

        <LayoutGridItem item xs={5}>
          <Typography>
            {t('placement.placementChange.newPlacementTitle')}
            {newPlacementId || missingField}
          </Typography>
        </LayoutGridItem>
        <LayoutGridItem item xs={5}>
          <ErrorMessage
            errors={errors}
            name={'errorMessage'}
            render={({ message }) => (
              <Typography color="error" variant="body2">
                {message}
              </Typography>
            )}
          />
        </LayoutGridItem>
      </LayoutGrid>
    </>
  );
};
